import { memo, useMemo, useState } from 'react';
import { SEVERITIES, countBySeverity, filterGroups, fixesForGroup, groupConcernsSize, type DiagnosticGroup } from '../diagnostics/group';
import type { CompileResult, Severity } from '../engine/types';
import { useI18n } from '../i18n';
import type { AnalysisState } from '../lib/analysis';
import { sizeName } from '../lib/sizes';
import { SeverityIcon } from '../ui/Icon';
import { AppliedFixes, Celebration, FindingCard, NoneUnderstood, type AppliedFix, type FindingActions } from './Findings';

interface Props {
  result: CompileResult;
  /** What the engine actually verified: 'none' forbids any success state. */
  analysis: AnalysisState;
  groups: DiagnosticGroup[];
  selectedSize: number | null;
  actions: FindingActions;
  applied: AppliedFix[];
  onUndo: (a: AppliedFix) => void;
  onDismissApplied: (a: AppliedFix) => void;
}

const SEV_CHIP: Record<Severity, string> = {
  error: 'aria-pressed:bg-error/10',
  warning: 'aria-pressed:bg-warning/10',
  info: 'aria-pressed:bg-info/10',
};

/** Compact view (tech editors): every finding in one dense list, filterable by severity. */
export const DiagnosticsTab = memo(function DiagnosticsTab({ result, analysis, groups, selectedSize, actions, applied, onUndo, onDismissApplied }: Props) {
  const { t, lang } = useI18n();
  const [severities, setSeverities] = useState<ReadonlySet<Severity>>(() => new Set(SEVERITIES));
  const fixesById = useMemo(() => new Map(result.fixes.map((f) => [f.id, f])), [result.fixes]);
  const linesByNo = useMemo(() => new Map(result.lines.map((l) => [l.line_no, l.text])), [result.lines]);
  const sizeScoped = useMemo(
    () => (selectedSize === null ? groups : groups.filter((g) => groupConcernsSize(g, selectedSize))),
    [groups, selectedSize],
  );
  const counts = useMemo(() => countBySeverity(sizeScoped), [sizeScoped]);
  const visible = useMemo(() => filterGroups(groups, { severities, size: selectedSize }), [groups, severities, selectedSize]);

  if (groups.length === 0) {
    return (
      <div className="space-y-3 p-4">
        <AppliedFixes items={applied} onUndo={onUndo} onDismiss={onDismissApplied} />
        {analysis === 'none' ? <NoneUnderstood /> : <Celebration />}
      </div>
    );
  }

  const toggle = (sev: Severity) =>
    setSeverities((prev) => {
      const next = new Set(prev);
      if (next.has(sev)) next.delete(sev);
      else next.add(sev);
      return next;
    });

  return (
    <div className="flex min-h-full flex-col">
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 border-b border-line bg-bg px-4 py-2" role="group" aria-label={t('diag.filter')}>
        {SEVERITIES.map((sev) => (
          <button
            key={sev}
            type="button"
            aria-pressed={severities.has(sev)}
            onClick={() => toggle(sev)}
            data-testid={`filter-${sev}`}
            className={`inline-flex h-7 items-center gap-2 rounded-pill px-3 text-xs font-semibold text-muted transition-colors hover:text-fg aria-pressed:text-fg ${SEV_CHIP[sev]}`}
          >
            <SeverityIcon severity={sev} size={12} className={severities.has(sev) ? '' : 'opacity-40'} />
            {t(`sevs.${sev}`)}
            <span className="tabular font-semibold">{counts[sev]}</span>
          </button>
        ))}
        {selectedSize !== null && (
          <span className="ml-auto rounded-pill bg-heather/[0.14] px-3 py-0.5 text-2xs font-semibold text-fg">
            {t('diag.sizeFiltered', { size: sizeName(result, selectedSize, lang) })}
          </span>
        )}
      </div>
      {analysis === 'none' && (
        <p className="mx-4 mt-3 flex items-start gap-2 rounded-card bg-warning/[0.10] px-3 py-2 text-xs text-fg" data-testid="diagnostics-none-note">
          <SeverityIcon severity="warning" size={13} className="mt-px" />
          {t('diag.noneNote')}
        </p>
      )}
      <div className="px-4 pt-3">
        <AppliedFixes items={applied} onUndo={onUndo} onDismiss={onDismissApplied} />
      </div>
      {visible.length === 0 ? (
        <p className="p-6 text-center text-sm text-muted">{t('diag.emptyFiltered')}</p>
      ) : (
        <ol className="space-y-2 p-4" data-testid="diagnostics-list">
          {visible.map((g) => (
            <li key={g.key}>
              <FindingCard
                group={g}
                result={result}
                fixes={fixesForGroup(g, fixesById)}
                sourceLine={linesByNo.get(g.line_no) ?? null}
                actions={actions}
                dense
              />
            </li>
          ))}
        </ol>
      )}
    </div>
  );
});
